import React, { useState, useEffect } from "react";
import FlagIcon from "../components/FlagIcon";import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Lock as LockIcon } from "@mui/icons-material";
import axios from "axios";

// Import flag images
import ArgentinaFlag from "../img/Argentina.png";
import BrazilFlag from "../img/Brazil.png";
import CroatiaFlag from "../img/Croatia.png";
import EgyptFlag from "../img/Egypt.png";
import GermanyFlag from "../img/Germany.png";
import ItalyFlag from "../img/Italy.png";
import MexicoFlag from "../img/Mexico.png";
import RestOfTheWorldFlag from "../img/RestOfTheWorld.png";
import PortugalFlag from "../img/portugal.png";
import SpainFlag from "../img/Spain.png";
import NationsBanner from "../img/nationsBanner.png";
import BabolatLogo from "../img/babolat.png";
import TTPadelLogo from "../img/ttennis.png";
import WinnersPhoto from "../img/winners.jfif";

import {
  NationsLeagueTeam,
  NationsLeagueMatch,
  NationsLeagueStandings,
  NationsLeagueStage,
} from "../types";
import SafeAvatar from "../components/SafeAvatar";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// The hard-coded "Champions!" celebration below was built for one specific edition
// (25/26, won by Germany) and shouldn't pop up claiming the same winner for new editions.
const LEGACY_CHAMPION_EVENT_ID = "spcup-of-nations-25-26";

// Flag image mapping
const flagImages: { [key: string]: string } = {
  Argentina: ArgentinaFlag,
  Brazil: BrazilFlag,
  Croatia: CroatiaFlag,
  Egypt: EgyptFlag,
  Germany: GermanyFlag,
  Italy: ItalyFlag,
  Mexico: MexicoFlag,
  "Rest of the World": RestOfTheWorldFlag,
  Portugal: PortugalFlag,
  Spain: SpainFlag,
};

const NationsLeaguePublic: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { nationsLeagueId } = useParams<{ nationsLeagueId: string }>();
  const [currentTab, setCurrentTab] = useState(0);
  const [teams, setTeams] = useState<NationsLeagueTeam[]>([]);
  const hasGroupC = teams.some((t) => t.group === "C");
  const koBase = hasGroupC ? 3 : 2; // first knockout tab index (Semifinals)
  const [matches, setMatches] = useState<NationsLeagueMatch[]>([]);
  const [standingsA, setStandingsA] = useState<NationsLeagueStandings[]>([]);
  const [standingsB, setStandingsB] = useState<NationsLeagueStandings[]>([]);
  const [standingsC, setStandingsC] = useState<NationsLeagueStandings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [announcement, setAnnouncement] = useState({
    message: "",
    isEnabled: false,
  });

  // Check if user is logged in as admin
  const isAdmin = () => {
    return !!localStorage.getItem("authToken");
  };

  // Roster dialog state
  const [rosterDialogOpen, setRosterDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<NationsLeagueTeam | null>(
    null
  );

  // Match details dialog state
  const [matchDetailsDialogOpen, setMatchDetailsDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<NationsLeagueMatch | null>(
    null
  );

  // Winner announcement modal state
  const [winnerModalOpen, setWinnerModalOpen] = useState(true);

  // Helper function to convert country code to flag emoji or return as-is if already emoji
  const getFlagEmoji = (flag: string): React.ReactNode => <FlagIcon flag={flag} />;

  useEffect(() => {
    if (nationsLeagueId) fetchAllData();
  }, [nationsLeagueId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError("");

      const [
        teamsRes,
        matchesRes,
        standingsARes,
        standingsBRes,
        standingsCRes,
        lockStatusRes,
        announcementRes,
      ] = await Promise.all([
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/teams`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/matches`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/standings/A`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/standings/B`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/standings/C`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/lock-status`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/announcement`),
      ]);

      const matchesData = matchesRes.data;
      setTeams(teamsRes.data);
      setMatches(matchesData);
      setStandingsA(standingsARes.data);
      setStandingsB(standingsBRes.data);
      setStandingsC(standingsCRes.data);
      setIsLocked(lockStatusRes.data.isLocked);
      setAnnouncement(announcementRes.data);

      // Determine default tab based on match status
      const semifinalMatches = matchesData.filter((m: NationsLeagueMatch) => 
        m.stage === "semifinal_top" || m.stage === "semifinal_bottom"
      );
      const finalMatches = matchesData.filter((m: NationsLeagueMatch) => 
        m.stage === "final_top" || m.stage === "final_bottom"
      );

      // Check if semifinals exist and if they're all completed
      const semifinalsExist = semifinalMatches.length > 0;
      const allSemifinalsComplete = semifinalMatches.length > 0 && 
        semifinalMatches.every((m: NationsLeagueMatch) => m.isCompleted);

      // Check if it's finals day or later (if any final has a date)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      const isFinalsDay = finalMatches.some((m: NationsLeagueMatch) => {
        if (!m.matchDateTime) return false;
        const matchDate = new Date(m.matchDateTime);
        matchDate.setHours(0, 0, 0, 0);
        return matchDate <= today;
      });

      // Set default tab: finals if semifinals complete and it's finals day, otherwise semifinals
      if (semifinalsExist) {
        if (allSemifinalsComplete && isFinalsDay) {
          setCurrentTab(teamsRes.data.some((t: NationsLeagueTeam) => t.group === "C") ? 5 : 4); // Finals tab
        } else {
          setCurrentTab(teamsRes.data.some((t: NationsLeagueTeam) => t.group === "C") ? 3 : 2); // Semifinals tab
        }
      }
      // If no semifinals exist yet, keep default tab 0 (Group A)
      
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to load Nations League data"
      );
    } finally {
      setLoading(false);
    }
  };

  const getMatchesForTab = (): NationsLeagueMatch[] => {
    if (currentTab === 0) {
      // Group A
      return matches.filter((m) => m.stage === "group" && m.group === "A");
    } else if (currentTab === 1) {
      // Group B
      return matches.filter((m) => m.stage === "group" && m.group === "B");
    } else if (hasGroupC && currentTab === 2) {
      // Group C
      return matches.filter((m) => m.stage === "group" && m.group === "C");
    } else if (currentTab === koBase) {
      // Semifinals (both top and bottom)
      return matches.filter((m) => m.stage === "semifinal_top" || m.stage === "semifinal_bottom");
    } else if (currentTab === koBase + 1) {
      // Third Place (both 3rd and 7th)
      return matches.filter((m) => m.stage === "third_place_top" || m.stage === "third_place_bottom");
    } else if (currentTab === koBase + 2) {
      // Finals (both top and bottom)
      return matches.filter((m) => m.stage === "final_top" || m.stage === "final_bottom");
    }
    return [];
  };

  const getTeamsForCurrentTab = (): NationsLeagueTeam[] => {
    if (currentTab === 0) {
      // Group A
      return teams.filter((t) => t.group === "A");
    } else if (currentTab === 1) {
      // Group B
      return teams.filter((t) => t.group === "B");
    } else if (hasGroupC && currentTab === 2) {
      // Group C
      return teams.filter((t) => t.group === "C");
    } else {
      // For knockout stages, get teams from the current matches
      const currentMatches = getMatchesForTab();
      const teamIds = new Set<string>();
      currentMatches.forEach((m) => {
        teamIds.add(m.team1Id);
        teamIds.add(m.team2Id);
      });
      return teams.filter((t) => teamIds.has(t.id));
    }
  };

  const renderCountryFlags = () => {
    const currentTeams = getTeamsForCurrentTab();

    if (currentTeams.length === 0) return null;

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)", // 2 columns on mobile
            sm: "repeat(4, 1fr)", // 4 columns on larger screens
          },
          gap: 3,
          mb: 4,
          p: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          justifyItems: "center",
        }}
      >
        {currentTeams.map((team, index) => (
          <Box
            key={team.id}
            sx={{
              position: "relative",
              animation: "fadeInUp 0.6s ease-out",
              animationDelay: `${index * 0.1}s`,
              animationFillMode: "both",
              "@keyframes fadeInUp": {
                from: {
                  opacity: 0,
                  transform: "translateY(20px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                cursor: "pointer",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-8px) scale(1.05)",
                  "& .flag-image": {
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)",
                  },
                },
              }}
              onClick={() => {
                setSelectedTeam(team);
                setRosterDialogOpen(true);
              }}
            >
              <Box
                className="flag-image"
                sx={{
                  width: { xs: 80, sm: 100, md: 120 },
                  height: { xs: 80, sm: 100, md: 120 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                  borderRadius: "50%",
                  border: "4px solid white",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                  bgcolor: "background.paper",
                  transition: "box-shadow 0.3s ease",
                }}
              >
                {getFlagEmoji(team.flag)}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: -24,
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "white",
                  fontWeight: "bold",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                }}
              >
                {team.name}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const handleOpenRosterDialog = (team: NationsLeagueTeam) => {
    setSelectedTeam(team);
    setRosterDialogOpen(true);
  };

  const renderStandingsTable = (standings: NationsLeagueStandings[]) => (
    <TableContainer component={Paper} sx={{ mb: 3, overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                position: "sticky",
                left: 0,
                backgroundColor: "background.paper",
                zIndex: 2,
                minWidth: { xs: "40px", sm: "50px" },
                maxWidth: { xs: "40px", sm: "50px" },
                width: { xs: "40px", sm: "50px" },
                px: { xs: 1, sm: 2 },
              }}
            >
              Pos
            </TableCell>
            <TableCell
              sx={{
                position: "sticky",
                left: { xs: "40px", sm: "50px" },
                backgroundColor: "background.paper",
                zIndex: 2,
                minWidth: { xs: "100px", sm: "150px" },
                maxWidth: { xs: "140px", sm: "200px" },
                boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
                px: { xs: 1, sm: 2 },
              }}
            >
              Team
            </TableCell>
            <TableCell align="center">Players</TableCell>
            <TableCell align="center">MP</TableCell>
            <TableCell align="center">MW</TableCell>
            <TableCell align="center">ML</TableCell>
            <TableCell align="center">SW</TableCell>
            <TableCell align="center">SL</TableCell>
            <TableCell align="center">SD</TableCell>
            <TableCell align="center">GW</TableCell>
            <TableCell align="center">GL</TableCell>
            <TableCell align="center">GD</TableCell>
            <TableCell align="center">Pts</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {standings.map((standing) => {
            const team = teams.find((t) => t.id === standing.teamId);
            return (
              <TableRow
                key={standing.teamId}
                onClick={() => team && handleOpenRosterDialog(team)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    "& td:not([data-sticky])": {
                      backgroundColor: "action.hover",
                    },
                    "& td[data-sticky]": {
                      backgroundColor: "action.hover",
                    },
                  },
                  "&:active": {
                    backgroundColor: "action.selected",
                    "& td[data-sticky]": {
                      backgroundColor: "action.selected",
                    },
                  },
                }}
              >
                <TableCell
                  data-sticky="true"
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: "background.paper",
                    zIndex: 2,
                    isolation: "isolate",
                    minWidth: { xs: "40px", sm: "50px" },
                    maxWidth: { xs: "40px", sm: "50px" },
                    width: { xs: "40px", sm: "50px" },
                    px: { xs: 1, sm: 2 },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "inherit",
                      zIndex: -1,
                    },
                    ".MuiTableRow-root:hover &": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "#1e1e1e !important"
                          : "#f5f5f5 !important",
                      zIndex: 3,
                    },
                    ".MuiTableRow-root:active &": {
                      backgroundColor: "action.selected !important",
                      zIndex: 3,
                    },
                  }}
                >
                  {standing.position}
                </TableCell>
                <TableCell
                  data-sticky="true"
                  sx={{
                    position: "sticky",
                    left: { xs: "40px", sm: "50px" },
                    backgroundColor: "background.paper",
                    zIndex: 2,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
                    isolation: "isolate",
                    minWidth: { xs: "100px", sm: "150px" },
                    maxWidth: { xs: "140px", sm: "200px" },
                    px: { xs: 1, sm: 2 },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "inherit",
                      zIndex: -1,
                    },
                    ".MuiTableRow-root:hover &": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "#1e1e1e !important"
                          : "#f5f5f5 !important",
                      zIndex: 3,
                    },
                    ".MuiTableRow-root:active &": {
                      backgroundColor: "action.selected !important",
                      zIndex: 3,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>{getFlagEmoji(standing.flag)}</span>
                    <span>{standing.teamName}</span>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {team?.players?.length || 0}
                </TableCell>
                <TableCell align="center">{standing.matchesPlayed}</TableCell>
                <TableCell align="center">{standing.matchesWon}</TableCell>
                <TableCell align="center">{standing.matchesLost}</TableCell>
                <TableCell align="center">{standing.setsWon}</TableCell>
                <TableCell align="center">{standing.setsLost}</TableCell>
                <TableCell align="center">
                  {standing.setDifference > 0 ? "+" : ""}
                  {standing.setDifference}
                </TableCell>
                <TableCell align="center">{standing.gamesWon}</TableCell>
                <TableCell align="center">{standing.gamesLost}</TableCell>
                <TableCell align="center">
                  {standing.gameDifference > 0 ? "+" : ""}
                  {standing.gameDifference}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {standing.points}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Mobile card view for matches
  const renderMatchesCards = (matches: NationsLeagueMatch[]) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {matches.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No matches yet</Typography>
        </Paper>
      ) : (
        matches.map((match) => (
          <Card
            key={match.id}
            onClick={() => {
              setSelectedMatch(match);
              setMatchDetailsDialogOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Match #{match.matchNumber || '-'}
                </Typography>
                {match.status === "Completed (Draw)" || match.isDraw ? (
                  <Chip label="🤝 Draw" color="info" size="small" />
                ) : match.status === "Completed" && match.winnerTeamId ? (
                  <Chip label="🏆 Completed" color="success" size="small" />
                ) : match.status === "In Progress" ? (
                  <Chip label="In Progress" color="warning" size="small" />
                ) : (
                  <Chip label="Scheduled" color="default" size="small" />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  {match.team1Flag && <span style={{ fontSize: '1.5rem' }}>{getFlagEmoji(match.team1Flag)}</span>}
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: match.isCompleted && match.team1MatchesWon > match.team2MatchesWon ? 'bold' : 'normal',
                    }}
                  >
                    {match.team1Name}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', mx: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {match.team1MatchesWon ?? 0} - {match.team2MatchesWon ?? 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (best of 3)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: match.isCompleted && match.team2MatchesWon > match.team1MatchesWon ? 'bold' : 'normal',
                    }}
                  >
                    {match.team2Name}
                  </Typography>
                  {match.team2Flag && <span style={{ fontSize: '1.5rem' }}>{getFlagEmoji(match.team2Flag)}</span>}
                </Box>
              </Box>

              {match.matchDateTime ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    📅 {new Date(match.matchDateTime).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    🕐 {new Date(match.matchDateTime).toLocaleString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" fontStyle="italic" sx={{ mb: 1 }}>
                  Date to be announced
                </Typography>
              )}

              {match.matches && match.matches.some(m => m.team1Player1 || m.team2Player1) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="primary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                    👆 Tap to see player details
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );

  const renderMatchesTable = (matches: NationsLeagueMatch[]) => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
              Matchup
            </TableCell>
            <TableCell>Teams</TableCell>
            <TableCell align="center">Result</TableCell>
            <TableCell>Date & Time</TableCell>
            <TableCell
              align="center"
              sx={{ display: { xs: "none", sm: "table-cell" } }}
            >
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {matches.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No matches yet
              </TableCell>
            </TableRow>
          ) : (
            matches.map((match) => {
              return (
                <TableRow
                  key={match.id}
                  onClick={() => {
                    setSelectedMatch(match);
                    setMatchDetailsDialogOpen(true);
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    #{match.matchNumber || "-"}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {match.team1Flag && <span>{getFlagEmoji(match.team1Flag)}</span>}
                      <strong
                        style={{
                          fontWeight:
                            match.isCompleted &&
                            match.team1MatchesWon > match.team2MatchesWon
                              ? "bold"
                              : "normal",
                          fontSize: "inherit",
                        }}
                      >
                        {match.team1Name}
                      </strong>
                      <span>vs</span>
                      <strong
                        style={{
                          fontWeight:
                            match.isCompleted &&
                            match.team2MatchesWon > match.team1MatchesWon
                              ? "bold"
                              : "normal",
                          fontSize: "inherit",
                        }}
                      >
                        {match.team2Name}
                      </strong>
                      {match.team2Flag && <span>{getFlagEmoji(match.team2Flag)}</span>}
                    </Box>
                    {/* Show match number on mobile as subtitle */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: { xs: "block", sm: "none" }, mt: 0.5 }}
                    >
                      Match #{match.matchNumber || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {match.team1MatchesWon ?? 0} -{" "}
                        {match.team2MatchesWon ?? 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: { xs: "none", sm: "block" } }}
                      >
                        (best of 3)
                      </Typography>
                      {/* Show player info indicator on mobile, full details on desktop */}
                      {match.matches &&
                        match.matches.some(
                          (m) => m.team1Player1 || m.team2Player1
                        ) && (
                          <>
                            {/* Mobile: Just show an indicator */}
                            <Box
                              sx={{
                                display: { xs: "block", md: "none" },
                                mt: 0.5,
                              }}
                            >
                              <Chip
                                label="Tap for players"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.65rem", height: "18px" }}
                              />
                            </Box>
                            {/* Desktop: Show full player details */}
                            <Box
                              sx={{
                                display: { xs: "none", md: "block" },
                                mt: 1,
                              }}
                            >
                              {match.matches.map((individualMatch, idx) => {
                                const hasPlayers =
                                  individualMatch.team1Player1 ||
                                  individualMatch.team2Player1 ||
                                  individualMatch.team1DidNotCompete ||
                                  individualMatch.team2DidNotCompete;
                                if (!hasPlayers) return null;

                                const team1Text =
                                  individualMatch.team1DidNotCompete
                                    ? "Did Not Compete"
                                    : `${
                                        individualMatch.team1Player1 || "?"
                                      } & ${
                                        individualMatch.team1Player2 || "?"
                                      }`;

                                const team2Text =
                                  individualMatch.team2DidNotCompete
                                    ? "Did Not Compete"
                                    : `${
                                        individualMatch.team2Player1 || "?"
                                      } & ${
                                        individualMatch.team2Player2 || "?"
                                      }`;

                                return (
                                  <Typography
                                    key={idx}
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.7rem" }}
                                  >
                                    M{individualMatch.matchNumber}: {team1Text}{" "}
                                    vs {team2Text}
                                  </Typography>
                                );
                              })}
                            </Box>
                          </>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {match.matchDateTime ? (
                      <>
                        {/* Desktop: Full date format */}
                        <Typography
                          variant="body2"
                          sx={{ display: { xs: "none", md: "block" } }}
                        >
                          {new Date(match.matchDateTime).toLocaleString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </Typography>
                        {/* Mobile: Compact format (date on one line, time on another) */}
                        <Box sx={{ display: { xs: "block", md: "none" } }}>
                          <Typography variant="caption" display="block">
                            {new Date(match.matchDateTime).toLocaleString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                              }
                            )}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {new Date(match.matchDateTime).toLocaleString(
                              "en-GB",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                        sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}
                      >
                        TBA
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ display: { xs: "none", sm: "table-cell" } }}
                  >
                    {match.status === "Completed (Draw)" || match.isDraw ? (
                      <Chip label="🤝 Completed - Draw" color="info" size="small" />
                    ) : match.status === "Completed" && match.winnerTeamId ? (
                      <Chip 
                        label={`🏆 Completed - ${match.winnerTeamId === match.team1Id ? match.team1Name : match.team2Name}`}
                        color="success" 
                        size="small" 
                      />
                    ) : match.status === "In Progress" ? (
                      <Chip label="In Progress" color="warning" size="small" />
                    ) : (
                      <Chip label="Scheduled" color="default" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  // Winner Announcement Modal
  const renderWinnerModal = () => (
    <Dialog
      open={winnerModalOpen}
      onClose={() => setWinnerModalOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: "linear-gradient(135deg, #1a237e 0%, #4a148c 50%, #b71c1c 100%)",
          color: "white",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
        },
      }}
    >
      {/* Confetti animation */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(circle at 20% 50%, rgba(255,215,0,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)",
          animation: "pulse 2s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.5 },
          },
          pointerEvents: "none",
        }}
      />
      
      <DialogContent sx={{ position: "relative", zIndex: 1, p: 4, textAlign: "center" }}>
        {/* Trophy Animation */}
        <Box
          sx={{
            fontSize: "5rem",
            mb: 2,
            animation: "bounce 1.5s ease-in-out infinite",
            "@keyframes bounce": {
              "0%, 100%": { transform: "translateY(0px) scale(1)" },
              "50%": { transform: "translateY(-20px) scale(1.1)" },
            },
          }}
        >
          🏆
        </Box>

        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            mb: 1,
            textShadow: "3px 3px 10px rgba(0,0,0,0.5)",
            animation: "fadeIn 1s ease-in",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(-20px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          SPCup of Nations 25/26
        </Typography>

        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            mb: 4,
            color: "#FFD700",
            textShadow: "2px 2px 8px rgba(0,0,0,0.7)",
          }}
        >
          CHAMPIONS! 🎉
        </Typography>

        {/* Winner Section */}
        <Box
          sx={{
            background: "rgba(255, 215, 0, 0.15)",
            borderRadius: 3,
            p: 3,
            mb: 3,
            border: "2px solid rgba(255, 215, 0, 0.5)",
            backdropFilter: "blur(10px)",
            animation: "glow 2s ease-in-out infinite",
            "@keyframes glow": {
              "0%, 100%": { boxShadow: "0 0 20px rgba(255, 215, 0, 0.3)" },
              "50%": { boxShadow: "0 0 40px rgba(255, 215, 0, 0.6)" },
            },
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
            🥇 Winner
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mb: 1,
            }}
          >
            <Box
              component="img"
              src={GermanyFlag}
              alt="Germany"
              sx={{
                width: 80,
                height: 60,
                objectFit: "cover",
                borderRadius: 1,
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                animation: "wiggle 2s ease-in-out infinite",
                "@keyframes wiggle": {
                  "0%, 100%": { transform: "rotate(0deg)" },
                  "25%": { transform: "rotate(-5deg)" },
                  "75%": { transform: "rotate(5deg)" },
                },
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: "bold",
                textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
              }}
            >
              Germany
            </Typography>
          </Box>
        </Box>

        {/* Runner-up Section */}
        <Box
          sx={{
            background: "rgba(192, 192, 192, 0.15)",
            borderRadius: 3,
            p: 3,
            border: "2px solid rgba(192, 192, 192, 0.4)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
            🥈 Runner-up
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={EgyptFlag}
              alt="Egypt"
              sx={{
                width: 80,
                height: 60,
                objectFit: "cover",
                borderRadius: 1,
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: "bold",
                textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
              }}
            >
              Egypt
            </Typography>
          </Box>
        </Box>

        {/* Fireworks effect */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "10%",
            fontSize: "2rem",
            animation: "firework1 3s ease-in-out infinite",
            "@keyframes firework1": {
              "0%": { opacity: 0, transform: "scale(0)" },
              "50%": { opacity: 1, transform: "scale(1)" },
              "100%": { opacity: 0, transform: "scale(1.5)" },
            },
          }}
        >
          ✨
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "10%",
            fontSize: "2rem",
            animation: "firework2 3s ease-in-out infinite 0.5s",
            "@keyframes firework2": {
              "0%": { opacity: 0, transform: "scale(0)" },
              "50%": { opacity: 1, transform: "scale(1)" },
              "100%": { opacity: 0, transform: "scale(1.5)" },
            },
          }}
        >
          ✨
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "center" }}>
        <Button
          onClick={() => setWinnerModalOpen(false)}
          variant="contained"
          size="large"
          sx={{
            background: "linear-gradient(45deg, #FFD700 30%, #FFA500 90%)",
            color: "#000",
            fontWeight: "bold",
            px: 4,
            py: 1.5,
            "&:hover": {
              background: "linear-gradient(45deg, #FFA500 30%, #FFD700 90%)",
            },
          }}
        >
          View Full Results
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, position: "relative" }}>
      {/* Lock Screen Overlay - Only show for non-admin users when locked */}
      {isLocked && !isAdmin() && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)",
            backdropFilter: "blur(20px)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 3,
            padding: 3,
            overflow: "hidden",
          }}
        >
          {/* Animated background flags */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gridTemplateRows: "repeat(3, 1fr)",
              gap: 4,
              padding: 4,
              opacity: 0.15,
              zIndex: 0,
            }}
          >
            {Object.values(flagImages).map((flag, index) => (
              <Box
                key={index}
                component="img"
                src={flag}
                alt={`Flag ${index}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  animation: `floatFlag ${
                    3 + index * 0.5
                  }s ease-in-out infinite`,
                  animationDelay: `${index * 0.2}s`,
                  "@keyframes floatFlag": {
                    "0%, 100%": {
                      transform: "translateY(0px) rotate(0deg) scale(1)",
                    },
                    "33%": {
                      transform: "translateY(-20px) rotate(5deg) scale(1.05)",
                    },
                    "66%": {
                      transform: "translateY(10px) rotate(-5deg) scale(0.95)",
                    },
                  },
                }}
              />
            ))}
          </Box>

          {/* Main content */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              animation: "fadeInScale 0.8s ease-out",
              "@keyframes fadeInScale": {
                from: {
                  opacity: 0,
                  transform: "scale(0.9)",
                },
                to: {
                  opacity: 1,
                  transform: "scale(1)",
                },
              },
            }}
          >
            {/* Lock icon with pulse animation */}
            <Box
              sx={{
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                  "50%": {
                    transform: "scale(1.1)",
                    opacity: 0.8,
                  },
                },
              }}
            >
              <LockIcon
                sx={{
                  fontSize: 100,
                  color: "white",
                  filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3))",
                }}
              />
            </Box>

            {/* Title with gradient and shine effect */}
            <Typography
              variant="h2"
              component="h1"
              align="center"
              sx={{
                fontWeight: "bold",
                color: "white",
                mb: 1,
                textShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                animation: "slideDown 0.8s ease-out",
                position: "relative",
                overflow: "hidden",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                  animation: "shine 3s infinite",
                },
                "@keyframes slideDown": {
                  from: {
                    opacity: 0,
                    transform: "translateY(-30px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
                "@keyframes shine": {
                  "0%": { left: "-100%" },
                  "50%, 100%": { left: "100%" },
                },
              }}
            >
              🏆 Nations League
            </Typography>

            {/* Tagline */}
            <Typography
              variant="h5"
              align="center"
              sx={{
                color: "white",
                fontWeight: 500,
                mb: 2,
                textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                animation: "fadeIn 1s ease-out 0.3s both",
                "@keyframes fadeIn": {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}
            >
              Nine nations. One game.
            </Typography>

            {/* Lock message */}
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: 3,
                padding: 3,
                maxWidth: 600,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                animation: "fadeInUp 1s ease-out 0.5s both",
                "@keyframes fadeInUp": {
                  from: {
                    opacity: 0,
                    transform: "translateY(20px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Page Currently Locked
              </Typography>
              <Typography
                variant="body1"
                align="center"
                sx={{
                  color: "text.secondary",
                }}
              >
                The tournament page will be available soon. Check back later for
                exciting matches and standings!
              </Typography>
            </Box>

            {/* Admin login info */}
            <Alert
              severity="info"
              sx={{
                maxWidth: 600,
                animation: "fadeIn 1.2s ease-out 0.8s both",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
              }}
            >
              <Typography variant="body2">
                <strong>Admins:</strong> You can{" "}
                <Button
                  size="small"
                  variant="text"
                  onClick={() => (window.location.href = "/admin/login")}
                  sx={{
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto",
                    verticalAlign: "baseline",
                    fontWeight: "bold",
                  }}
                >
                  log in here
                </Button>{" "}
                to view and manage the page even while it's locked.
              </Typography>
            </Alert>
          </Box>
        </Box>
      )}

      {/* Announcement Marquee */}
      {announcement.isEnabled && announcement.message && (
        <Box
          sx={{
            backgroundColor: "#667eea",
            color: "white",
            py: 1.5,
            mb: 3,
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
          }}
        >
          <Box
            sx={{
              display: "inline-block",
              whiteSpace: "nowrap",
              animation: "marquee 20s linear infinite",
              paddingLeft: "100%",
              "@keyframes marquee": {
                "0%": {
                  transform: "translateX(0)",
                },
                "100%": {
                  transform: "translateX(-100%)",
                },
              },
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                display: "inline-block",
                fontWeight: 600,
                px: 4,
              }}
            >
              📢 {announcement.message}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Creative Header with Floating Banner */}
      <Box
        sx={{
          position: "relative",
          mb: 4,
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* Title Section */}
        <Box sx={{ flex: 1, minWidth: { xs: "100%", md: "auto" } }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: "bold",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
              animation: "slideInLeft 0.8s ease-out",
              "@keyframes slideInLeft": {
                from: {
                  opacity: 0,
                  transform: "translateX(-30px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateX(0)",
                },
              },
            }}
          >
            🏆 Stuttgart Padel Cup of Nations
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{
              animation: "fadeIn 1s ease-out 0.3s both",
              "@keyframes fadeIn": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
          >
            Nine nations. One game.
          </Typography>

          {/* Sponsor Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 2,
              animation: "fadeIn 1s ease-out 0.5s both",
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.85rem",
              }}
            >
              Sponsored by
            </Typography>
            <Box
              component="img"
              src={BabolatLogo}
              alt="Babolat"
              sx={{
                height: { xs: 30, sm: 35, md: 40 },
                width: "auto",
                objectFit: "contain",
                filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
              }}
            />
            {nationsLeagueId === LEGACY_CHAMPION_EVENT_ID && (
              <Box
                component="img"
                src={TTPadelLogo}
                alt="TTpadel"
                sx={{
                  height: { xs: 60, sm: 75, md: 90 },
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
                }}
              />
            )}
          </Box>
        </Box>

        {/* Floating Banner Card */}
        <Box
          sx={{
            position: "relative",
            width: { xs: "180px", sm: "280px", md: "320px" },
            height: { xs: "100px", sm: "160px", md: "180px" },
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
            animation: "floatIn 1s ease-out 0.2s both",
            mx: { xs: "auto", md: 0 },
            "@keyframes floatIn": {
              from: {
                opacity: 0,
                transform: "translateY(-30px) rotate(-5deg)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0) rotate(0deg)",
              },
            },
            "&:hover": {
              transform: "translateY(-8px) rotate(2deg)",
              transition: "transform 0.4s ease",
              boxShadow: "0 12px 40px rgba(102, 126, 234, 0.5)",
            },
          }}
        >
          <Box
            component="img"
            src={NationsBanner}
            alt="Stuttgart Padel Cup of Nations"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
          {/* Shine effect overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "-100%",
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
              animation: "shine 3s infinite",
              "@keyframes shine": {
                "0%": { left: "-100%" },
                "50%": { left: "100%" },
                "100%": { left: "100%" },
              },
            }}
          />
        </Box>
      </Box>

      {/* Help Text */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          💡 <strong>Quick Guide:</strong> Click on a <strong>team name</strong>{" "}
          to view registered players. Click on a <strong>match</strong> to see
          detailed results and player matchups.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {teams.length === 0 ? (
        <Alert severity="info">
          The Stuttgart Padel Cup of Nations tournament has not started yet.
          Check back soon!
        </Alert>
      ) : (
        <>
          <Paper sx={{ mb: 3 }}>
            <Box
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
                "&::-webkit-scrollbar": {
                  height: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                },
              }}
            >
              <Tabs
                value={currentTab}
                onChange={(_, v) => setCurrentTab(v)}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTab-root": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    minWidth: { xs: 70, sm: 120 },
                    px: { xs: 0.5, sm: 2 },
                  },
                  "& .MuiTabs-scrollButtons": {
                    "&.Mui-disabled": { opacity: 0.3 },
                  },
                }}
              >
                <Tab label="Group A" />
                <Tab label="Group B" />
                {hasGroupC && <Tab label="Group C" />}
                <Tab label="Semifinals" />
                <Tab label="3rd/7th Place" />
                <Tab label="Finals" />
              </Tabs>
            </Box>
          </Paper>

          <Box sx={{ mt: 3 }}>
            {/* Render country flags for current tab */}
            {renderCountryFlags()}

            {currentTab === 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Group A Standings
                </Typography>
                {renderStandingsTable(standingsA)}
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                  Group A Matches
                </Typography>
                {isMobile ? renderMatchesCards(getMatchesForTab()) : renderMatchesTable(getMatchesForTab())}
              </>
            )}

            {currentTab === 1 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Group B Standings
                </Typography>
                {renderStandingsTable(standingsB)}
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                  Group B Matches
                </Typography>
                {isMobile ? renderMatchesCards(getMatchesForTab()) : renderMatchesTable(getMatchesForTab())}
              </>
            )}

            {hasGroupC && currentTab === 2 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Group C Standings
                </Typography>
                {renderStandingsTable(standingsC)}
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                  Group C Matches
                </Typography>
                {isMobile ? renderMatchesCards(getMatchesForTab()) : renderMatchesTable(getMatchesForTab())}
              </>
            )}

            {currentTab === koBase && (
              <>
                {/* Semifinals - Top Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
                  Semifinals - Top Bracket (Positions 1-4)
                </Typography>
                {isMobile ? renderMatchesCards(
                  matches.filter((m) => m.stage === "semifinal_top")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "semifinal_top")
                )}

                {/* Semifinals - Bottom Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
                  Semifinals - Bottom Bracket (Positions 5-8)
                </Typography>
                {isMobile ? renderMatchesCards(
                  matches.filter((m) => m.stage === "semifinal_bottom")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "semifinal_bottom")
                )}
              </>
            )}

            {currentTab === koBase + 1 && (
              <>
                {/* 3rd Place Match - Top Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
                  3rd Place Match (Positions 3-4)
                </Typography>
                {isMobile ? renderMatchesCards(
                  matches.filter((m) => m.stage === "third_place_top")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "third_place_top")
                )}

                {/* 7th Place Match - Bottom Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
                  7th Place Match (Positions 7-8)
                </Typography>
                {isMobile ? renderMatchesCards(
                  matches.filter((m) => m.stage === "third_place_bottom")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "third_place_bottom")
                )}
              </>
            )}

            {currentTab === koBase + 2 && (
              <>
                {/* Final - Top Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
                  Final - Top Bracket (Positions 1-2)
                </Typography>
                {isMobile ? renderMatchesCards(
                  matches.filter((m) => m.stage === "final_top")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "final_top")
                )}

                {/* Final - Bottom Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
                  Final - Bottom Bracket (Positions 5-6)
                </Typography>
                {isMobile ? renderMatchesCards(
                  matches.filter((m) => m.stage === "final_bottom")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "final_bottom")
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* Roster Dialog */}
      <Dialog
        open={rosterDialogOpen}
        onClose={() => setRosterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Team Roster
          {selectedTeam && (
            <Typography variant="body2" color="text.secondary">
              {getFlagEmoji(selectedTeam.flag)} {selectedTeam.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Players ({selectedTeam?.players?.length || 0})
            </Typography>
            <List>
              {!selectedTeam?.players || selectedTeam.players.length === 0 ? (
                <ListItem>
                  <ListItemText secondary="No players added yet" />
                </ListItem>
              ) : (
                selectedTeam.players.map((player, index) => {
                  // Backward compatibility: handle both string and object format
                  const playerName =
                    typeof player === "string" ? player : player.name;
                  const playerPhoto =
                    typeof player === "string"
                      ? undefined
                      : player.profilePicture;

                  return (
                    <ListItem key={index}>
                      <ListItemAvatar>
                        <SafeAvatar
                          src={playerPhoto}
                          alt={playerName}
                          fallbackText={playerName}
                          sx={{ width: 40, height: 40 }}
                        />
                      </ListItemAvatar>
                      <ListItemText primary={`${index + 1}. ${playerName}`} />
                    </ListItem>
                  );
                })
              )}
            </List>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Match Details Dialog */}
      <Dialog
        open={matchDetailsDialogOpen}
        onClose={() => setMatchDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Match Details
          {selectedMatch && (
            <Typography variant="body2" color="text.secondary">
              {selectedMatch.team1Flag && getFlagEmoji(selectedMatch.team1Flag)} {selectedMatch.team1Name}{" "}
              vs {selectedMatch.team2Name}{" "}
              {selectedMatch.team2Flag && getFlagEmoji(selectedMatch.team2Flag)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedMatch && (
            <Box sx={{ mt: 2 }}>
              {/* Overall Result */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Overall Result
                </Typography>
                <Typography variant="h4" align="center" sx={{ my: 2 }}>
                  {selectedMatch.team1MatchesWon ?? 0} -{" "}
                  {selectedMatch.team2MatchesWon ?? 0}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  (Best of 3 matches)
                </Typography>
              </Box>

              {/* Individual Matches */}
              {selectedMatch.matches?.map((individualMatch, idx) => {
                const hasScores = individualMatch.sets.some(
                  (set) => set.team1Games > 0 || set.team2Games > 0
                );

                return (
                  <Box
                    key={idx}
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Match {individualMatch.matchNumber}
                      {individualMatch.isCompleted &&
                        individualMatch.winnerTeamId && (
                          <Chip
                            label={`Winner: ${
                              individualMatch.winnerTeamId ===
                              selectedMatch.team1Id
                                ? selectedMatch.team1Name
                                : selectedMatch.team2Name
                            }`}
                            color="success"
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        )}
                    </Typography>

                    {/* Players */}
                    {(individualMatch.team1Player1 ||
                      individualMatch.team2Player1 ||
                      individualMatch.team1DidNotCompete ||
                      individualMatch.team2DidNotCompete) && (
                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {selectedMatch.team1Name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {individualMatch.team1DidNotCompete
                              ? "Did Not Compete"
                              : `${individualMatch.team1Player1 || "TBD"} & ${
                                  individualMatch.team1Player2 || "TBD"
                                }`}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mx: 2 }}>
                          vs
                        </Typography>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {selectedMatch.team2Name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {individualMatch.team2DidNotCompete
                              ? "Did Not Compete"
                              : `${individualMatch.team2Player1 || "TBD"} & ${
                                  individualMatch.team2Player2 || "TBD"
                                }`}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Sets */}
                    {hasScores ? (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Sets
                        </Typography>
                        {individualMatch.sets.map((set, setIdx) => {
                          const isPlayed =
                            set.team1Games > 0 || set.team2Games > 0;
                          if (!isPlayed) return null;

                          return (
                            <Box
                              key={setIdx}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 1,
                                mb: 1,
                                bgcolor: "action.hover",
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="body2">
                                Set {setIdx + 1}
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {set.team1Games} - {set.team2Games}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                      >
                        Scores not yet available
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDetailsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Winner Announcement Modal */}
      <Dialog
        open={winnerModalOpen && nationsLeagueId === LEGACY_CHAMPION_EVENT_ID}
        onClose={() => setWinnerModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #1a237e 0%, #4a148c 50%, #b71c1c 100%)",
            color: "white",
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle at 20% 50%, rgba(255,215,0,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)",
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.5 },
            },
            pointerEvents: "none",
          }}
        />

        {/* Confetti Animation */}
        {[...Array(30)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: -10,
              left: `${Math.random() * 100}%`,
              width: 10,
              height: 10,
              backgroundColor: [
                "#FFD700",
                "#FFA500",
                "#FF6B6B",
                "#4ECDC4",
                "#95E1D3",
                "#F38181",
              ][i % 6],
              opacity: 0.8,
              borderRadius: i % 3 === 0 ? "50%" : "2px",
              animation: `confetti${i % 3} ${3 + Math.random() * 2}s linear infinite`,
              animationDelay: `${Math.random() * 2}s`,
              transform: `rotate(${Math.random() * 360}deg)`,
              "@keyframes confetti0": {
                "0%": { transform: "translateY(0) rotate(0deg)", opacity: 0 },
                "10%": { opacity: 0.8 },
                "100%": { transform: "translateY(100vh) rotate(360deg)", opacity: 0 },
              },
              "@keyframes confetti1": {
                "0%": { transform: "translateY(0) rotate(0deg) translateX(0)", opacity: 0 },
                "10%": { opacity: 0.8 },
                "100%": { transform: "translateY(100vh) rotate(-360deg) translateX(100px)", opacity: 0 },
              },
              "@keyframes confetti2": {
                "0%": { transform: "translateY(0) rotate(0deg) translateX(0)", opacity: 0 },
                "10%": { opacity: 0.8 },
                "100%": { transform: "translateY(100vh) rotate(360deg) translateX(-100px)", opacity: 0 },
              },
              pointerEvents: "none",
              zIndex: 10,
            }}
          />
        ))}
        
        <DialogContent sx={{ position: "relative", zIndex: 1, p: 4, textAlign: "center", overflowY: "auto", overflowX: "hidden" }}>
          <Box
            sx={{
              fontSize: "5rem",
              mb: 2,
              animation: "bounce 1.5s ease-in-out infinite",
              "@keyframes bounce": {
                "0%, 100%": { transform: "translateY(0px) scale(1)" },
                "50%": { transform: "translateY(-20px) scale(1.1)" },
              },
            }}
          >
            🏆
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              mb: 1,
              textShadow: "3px 3px 10px rgba(0,0,0,0.5)",
              animation: "fadeIn 1s ease-in",
              "@keyframes fadeIn": {
                from: { opacity: 0, transform: "translateY(-20px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            SPCup of Nations 25/26
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              mb: 4,
              color: "#FFD700",
              textShadow: "2px 2px 8px rgba(0,0,0,0.7)",
            }}
          >
            CHAMPIONS! 🎉
          </Typography>

          <Box
            component="img"
            src={WinnersPhoto}
            alt="Winners and Runners-up"
            sx={{
              width: "100%",
              maxWidth: 450,
              height: "auto",
              borderRadius: 2,
              mb: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              border: "3px solid rgba(255, 215, 0, 0.6)",
            }}
          />

          <Box
            sx={{
              background: "rgba(255, 215, 0, 0.15)",
              borderRadius: 3,
              p: 3,
              mb: 3,
              border: "2px solid rgba(255, 215, 0, 0.5)",
              backdropFilter: "blur(10px)",
              animation: "glow 2s ease-in-out infinite",
              "@keyframes glow": {
                "0%, 100%": { boxShadow: "0 0 20px rgba(255, 215, 0, 0.3)" },
                "50%": { boxShadow: "0 0 40px rgba(255, 215, 0, 0.6)" },
              },
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
              🥇 Winner
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mb: 1,
              }}
            >
              <Box
                component="img"
                src={GermanyFlag}
                alt="Germany"
                sx={{
                  width: 80,
                  height: 60,
                  objectFit: "cover",
                  borderRadius: 1,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  animation: "wiggle 2s ease-in-out infinite",
                  "@keyframes wiggle": {
                    "0%, 100%": { transform: "rotate(0deg)" },
                    "25%": { transform: "rotate(-3deg)" },
                    "75%": { transform: "rotate(3deg)" },
                  },
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.25rem" },
                }}
              >
                Germany
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              background: "rgba(192, 192, 192, 0.15)",
              borderRadius: 3,
              p: 3,
              border: "2px solid rgba(192, 192, 192, 0.4)",
              overflow: "hidden",
              backdropFilter: "blur(10px)",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
              🥈 Runner-up
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Box
                component="img"
                src={EgyptFlag}
                alt="Egypt"
                sx={{
                  width: 80,
                  height: 60,
                  objectFit: "cover",
                  borderRadius: 1,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.25rem" },
                }}
              >
                Egypt
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: "10%",
              left: "10%",
              fontSize: "2rem",
              animation: "firework1 3s ease-in-out infinite",
              "@keyframes firework1": {
                "0%": { opacity: 0, transform: "scale(0)" },
                "50%": { opacity: 1, transform: "scale(1)" },
                "100%": { opacity: 0, transform: "scale(1.5)" },
              },
            }}
          >
            ✨
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              right: "10%",
              fontSize: "2rem",
              animation: "firework2 3s ease-in-out infinite 0.5s",
              "@keyframes firework2": {
                "0%": { opacity: 0, transform: "scale(0)" },
                "50%": { opacity: 1, transform: "scale(1)" },
                "100%": { opacity: 0, transform: "scale(1.5)" },
              },
            }}
          >
            ✨
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: "center" }}>
          <Button
            onClick={() => setWinnerModalOpen(false)}
            variant="contained"
            size="large"
            sx={{
              background: "linear-gradient(45deg, #FFD700 30%, #FFA500 90%)",
              color: "#000",
              fontWeight: "bold",
              px: 4,
              py: 1.5,
              "&:hover": {
                background: "linear-gradient(45deg, #FFA500 30%, #FFD700 90%)",
              },
            }}
          >
            View Full Results
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NationsLeaguePublic;
